import React, { useState, useEffect } from 'react';
import { FaSearch, FaSave, FaChevronLeft, FaChevronRight, FaFilter, FaTimes, FaStar } from 'react-icons/fa';
import './css_pages/ProductsByStore.css';
import { getPrepsByStore, updatePrepParReorder } from '../services/prepByStoreService';
import { useStore } from '../contexts/StoreContext';

const ITEMS_PER_PAGE  = 25;
const INITIAL_FILTERS = { missingFields: false };

// ════════════════════════════════════════════════════════════════
function PrepByStore() {
  const { selectedStore } = useStore();
  const selectedStoreId   = selectedStore?.id;

  const [preps, setPreps]                 = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [currentPage, setCurrentPage]     = useState(1);
  const [editMode, setEditMode]           = useState(false);
  const [editedData, setEditedData]       = useState({});
  const [searchTerm, setSearchTerm]       = useState('');
  const [activeFilters, setActiveFilters] = useState(INITIAL_FILTERS);

  useEffect(() => {
    if (selectedStoreId) { fetchPreps(); setEditMode(false); setCurrentPage(1); }
  }, [selectedStoreId]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, activeFilters]);

  // ── Fetch ──
  const fetchPreps = async () => {
    if (!selectedStoreId) return;
    try {
      setLoading(true); setError(null);
      const r = await getPrepsByStore(selectedStoreId);
      if (r.success) {
        setPreps(r.data);
        const init = {};
        r.data.forEach(item => { init[item.id] = { par: item.par || '', reorderPoint: item.reorderPoint || '' }; });
        setEditedData(init);
      }
    } catch { setError('Error loading preparations'); }
    finally { setLoading(false); }
  };

  // ── Edit mode ──
  const handleToggleEditMode = () => {
    if (editMode) fetchPreps();
    setEditMode(!editMode);
  };
  const handleEditFieldChange = (itemId, field, value) =>
    setEditedData(prev => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));

  const handleSaveBulkChanges = async () => {
    try {
      setLoading(true);
      await Promise.all(Object.keys(editedData).map(itemId => {
        const d = editedData[itemId];
        return updatePrepParReorder(parseInt(itemId), d.par ? parseFloat(d.par) : null, d.reorderPoint ? parseFloat(d.reorderPoint) : null);
      }));
      alert('All changes saved successfully');
      setEditMode(false); await fetchPreps();
    } catch { alert('Error saving changes'); }
    finally { setLoading(false); }
  };

  // ── Filters & pagination ──
  const toggleFilter    = (key) => setActiveFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const clearFilters    = () => setActiveFilters(INITIAL_FILTERS);
  const anyFilterActive = Object.values(activeFilters).some(Boolean);

  const filteredPreps = preps.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (activeFilters.missingFields && !(!item.par || !item.reorderPoint)) return false;
    return true;
  });

  const totalPages      = Math.ceil(filteredPreps.length / ITEMS_PER_PAGE);
  const startIndex      = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentPreps    = filteredPreps.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else if (currentPage <= 3)              { pages.push(1,2,3,4,'...',totalPages); }
    else if (currentPage >= totalPages - 2) { pages.push(1,'...',totalPages-3,totalPages-2,totalPages-1,totalPages); }
    else { pages.push(1,'...',currentPage-1,currentPage,currentPage+1,'...',totalPages); }
    return pages;
  };

  const countMissingFields = preps.filter(i => !i.par || !i.reorderPoint).length;
  const colCount           = editMode ? 3 : 3;

  // ── Desktop table row ──
  const renderPrepRow = (item) => {
    const isWarning = activeFilters.missingFields && (!item.par || !item.reorderPoint);
    return (
      <tr key={item.id} className={isWarning ? 'pbs-row-highlight-warning' : ''}>
        <td>
          {item.name}
          {item.mainIngredientName && (
            <span className="pbs-product-card-container" style={{ marginLeft: 8 }}>
              <FaStar style={{ fontSize: 10, marginRight: 3, color: '#f59e0b' }} />{item.mainIngredientName}
            </span>
          )}
        </td>
        <td>{editMode ? <input type="number" step="1" className="pbs-inline-edit-input" value={editedData[item.id]?.par || ''} onChange={e => handleEditFieldChange(item.id, 'par', e.target.value)} /> : (item.par || <span className="pbs-missing-badge">—</span>)}</td>
        <td>{editMode ? <input type="number" step="1" className="pbs-inline-edit-input" value={editedData[item.id]?.reorderPoint || ''} onChange={e => handleEditFieldChange(item.id, 'reorderPoint', e.target.value)} /> : (item.reorderPoint || <span className="pbs-missing-badge">—</span>)}</td>
      </tr>
    );
  };

  // ── Mobile card ──
  const renderMobileCard = (item) => {
    const isWarning = activeFilters.missingFields && (!item.par || !item.reorderPoint);
    const ed = editedData[item.id] || {};
    return (
      <div key={item.id} className={`pbs-product-card${isWarning ? ' pbs-product-card--warn' : ''}`}>
        <div className="pbs-product-card-header">
          <div className="pbs-product-card-name">{item.name}</div>
        </div>
        {item.mainIngredientName && (
          <div className="pbs-product-card-meta">
            <span className="pbs-product-card-container"><FaStar style={{ fontSize: 10, marginRight: 3, color: '#f59e0b' }} />{item.mainIngredientName}</span>
          </div>
        )}
        <div className="pbs-product-card-fields">
          <div className="pbs-product-card-field">
            <span className="pbs-product-card-label">Par</span>
            {editMode
              ? <input type="number" step="1" className="pbs-card-edit-input" value={ed.par || ''} onChange={e => handleEditFieldChange(item.id, 'par', e.target.value)} placeholder="—" />
              : <span className="pbs-product-card-value">{item.par || <span className="pbs-missing-badge">—</span>}</span>}
          </div>
          <div className="pbs-product-card-field">
            <span className="pbs-product-card-label">Reorder</span>
            {editMode
              ? <input type="number" step="1" className="pbs-card-edit-input" value={ed.reorderPoint || ''} onChange={e => handleEditFieldChange(item.id, 'reorderPoint', e.target.value)} placeholder="—" />
              : <span className="pbs-product-card-value">{item.reorderPoint || <span className="pbs-missing-badge">—</span>}</span>}
          </div>
        </div>
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════════
  return (
    <div className="pbs-page">

      {/* ══ HEADER ══ */}
      <div className="pbs-page-header pbs-sticky-header">
        <div className="pbs-header-left">
          <h1 className="pbs-page-title">Pre-Batch by Store</h1>
          {selectedStore && <span className="pbs-store-badge">{selectedStore.name}</span>}
        </div>

        {/* Desktop actions */}
        <div className="pbs-header-actions pbs-desktop-only">
          {editMode && (
            <button className="pbs-btn-save-bulk" onClick={handleSaveBulkChanges} disabled={loading}>
              <FaSave /> Save All Changes
            </button>
          )}
          <button className="pbs-btn-toggle-edit" onClick={handleToggleEditMode} disabled={loading || !selectedStoreId}>
            {editMode ? 'Cancel Edit Mode' : 'Toggle Edit Mode'}
          </button>
        </div>

        {/* Mobile actions */}
        <div className="pbs-mobile-header-right pbs-mobile-only">
          {editMode ? (
            <>
              <button className="pbs-btn-save-bulk pbs-btn-save-mobile" onClick={handleSaveBulkChanges} disabled={loading}>
                <FaSave /> Save
              </button>
              <button className="pbs-btn-toggle-edit pbs-btn-cancel-mobile" onClick={handleToggleEditMode} disabled={loading}>
                Cancel
              </button>
            </>
          ) : (
            <button className="pbs-btn-toggle-edit" onClick={handleToggleEditMode} disabled={loading || !selectedStoreId}>
              Edit Mode
            </button>
          )}
        </div>
      </div>

      {error && <div className="pbs-error-msg">{error}<button onClick={fetchPreps}>Retry</button></div>}

      {selectedStoreId ? (
        <>
          {/* ── SEARCH + FILTERS ── */}
          <div className="pbs-search-section">
            <div className="pbs-search-bar">
              <input type="text" placeholder="Search by preparation name…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} disabled={editMode} />
              <button className="pbs-btn-search" disabled={editMode}><FaSearch /> Search</button>
            </div>
            <div className="pbs-filter-bar">
              <span className="pbs-filter-label"><FaFilter /> Filters:</span>
              <button className={`pbs-filter-chip ${activeFilters.missingFields ? 'active' : ''}`} onClick={() => toggleFilter('missingFields')} disabled={editMode}>
                Missing Par / Reorder
                {countMissingFields > 0 && <span className="pbs-filter-chip-count">{countMissingFields}</span>}
              </button>
              {anyFilterActive && <button className="pbs-filter-clear" onClick={clearFilters} disabled={editMode}><FaTimes /> Clear filters</button>}
            </div>
          </div>

          {/* ── PAGINATION INFO ── */}
          {filteredPreps.length > 0 && (
            <div className="pbs-pagination-info">
              <span>Total: {filteredPreps.length}</span>
              {anyFilterActive && <span className="pbs-filter-active-note">(filtered from {preps.length})</span>}
              <span className="pbs-sep">|</span>
              <span>Showing {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, filteredPreps.length)} of {filteredPreps.length}</span>
            </div>
          )}

          {/* ══ TABLE — desktop ══ */}
          <div className="pbs-table-wrap pbs-desktop-only">
            {loading && <div className="pbs-loading">Loading…</div>}
            <table className="pbs-data-table">
              <thead>
                <tr>
                  <th>Preparation</th><th>Par</th><th>Reorder Point</th>
                </tr>
              </thead>
              <tbody>
                {currentPreps.length === 0
                  ? <tr><td colSpan={colCount} style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>{loading ? 'Loading…' : 'No preparations found'}</td></tr>
                  : currentPreps.map(renderPrepRow)}
              </tbody>
            </table>
          </div>

          {/* ══ CARDS — mobile ══ */}
          <div className="pbs-cards-wrap pbs-mobile-only">
            {loading && <div className="pbs-loading">Loading…</div>}
            {!loading && (currentPreps.length === 0
              ? <div className="pbs-loading">No preparations found</div>
              : currentPreps.map(renderMobileCard))}
          </div>

          {/* ── PAGINATION ── */}
          {totalPages > 1 && (
            <>
              <div className="pbs-pagination-controls pbs-desktop-only">
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟨⟨</button>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage === 1}><FaChevronLeft /></button>
                <div className="pbs-pagination-numbers">
                  {getPageNumbers().map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="pbs-pagination-ellipsis">…</span>
                      : <button key={page} className={`pbs-pagination-number ${currentPage === page ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </div>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))} disabled={currentPage === totalPages}><FaChevronRight /></button>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>⟩⟩</button>
              </div>

              <div className="pbs-pagination-mobile pbs-mobile-only">
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage === 1}>
                  <FaChevronLeft /> Prev
                </button>
                <span className="pbs-pagination-mobile-info">Page {currentPage} of {totalPages}</span>
                <button className="pbs-pagination-btn" onClick={() => setCurrentPage(p => Math.min(p+1,totalPages))} disabled={currentPage === totalPages}>
                  Next <FaChevronRight />
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="pbs-no-store-msg"><p>Please select a store from the sidebar to view its preparations</p></div>
      )}
    </div>
  );
}

export default PrepByStore;